import {
  baseKeymap,
  chainCommands,
  deleteSelection,
  exitCode,
  joinBackward,
  joinDown,
  joinUp,
  lift,
  selectNodeBackward,
  selectParentNode,
  setBlockType,
  wrapIn,
} from 'prosemirror-commands';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { ellipsis, emDash, inputRules, smartQuotes, undoInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import type { Slice } from 'prosemirror-model';
import { DOMParser } from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import { Plugin, Selection } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import { WysiwygShortcuts } from '@core/wysiwyg-legacy';

import { handlePaste } from './handle-paste';
import type { BasicPluginSchema } from './nodes';
import { ParagraphNode } from './nodes';
import type {
  EditorButton,
  EditorMode,
  ProsemirrorCommand,
  ProsemirrorDispatch,
  RtPMPlugin,
} from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY, isMacOS, metaKey } from '../../helpers/utils';
import { NodeName } from '../../schema/nodes';

export class BasicPlugin implements RtPMPlugin {
  public buttons = {
    undo: <EditorButton>{
      icon: 'undo',
      tooltipText: 'wysiwyg.undo',
      tooltipShortcut: WysiwygShortcuts.Undo(metaKey),
      isActive: () => false,
      run: (state, dispatch) => {
        undo(state, dispatch);
        this.editorView.focus();
      },
    },
    redo: <EditorButton>{
      icon: 'redo',
      tooltipText: 'wysiwyg.redo',
      tooltipShortcut: WysiwygShortcuts.Redo(metaKey),
      isActive: () => false,
      run: (state, dispatch) => {
        redo(state, dispatch);
        this.editorView.focus();
      },
    },
  };

  private editorView: EditorView;
  private basePlugin = new Plugin({
    props: {
      // This is default implementation but with single newline as block separator
      clipboardTextSerializer: (s: Slice) => s.content.textBetween(0, s.content.size, '\n'),
      // This is default implementation but doesnt ignore \n\n etc
      clipboardTextParser: (text, $context, plain) => {
        const textArr = text.split(/(?:\r\n?|\n)/);
        const dom = document.createElement('div');
        const childNodes = textArr.map(text => {
          const paragraph = document.createElement('p');
          paragraph.textContent = text;
          return paragraph;
        });
        childNodes.forEach(node => dom.appendChild(node));
        const parser: DOMParser =
          this.editorView.someProp('clipboardParser') ||
          this.editorView.someProp('domParser') ||
          DOMParser.fromSchema(this.editorView.state.schema);

        return parser.parseSlice(dom);
      },
      handlePaste: handlePaste,
    },
  });

  constructor(private options?: { enterAsHardbreak: boolean }) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: BasicPluginSchema) {
    return [
      history(),
      dropCursor(),
      gapCursor(),
      this.basePlugin,
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY - 1,
        plugin: keymap({
          ...baseKeymap,
          Backspace: (...args) =>
            chainCommands(
              deleteSelection,
              this.afterListBackspace.bind(this),
              joinBackward,
              selectNodeBackward,
            )(...args),
        }),
      },
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY,
        plugin: inputRules({
          rules: [...smartQuotes, emDash, ellipsis],
        }),
      },
    ];
  }

  private afterListBackspace(state: EditorState, dispatch?: ProsemirrorDispatch) {
    const { from } = state.selection;
    const { node: currentNode, offset: currentNodeOffset } = state.doc.childBefore(from);
    const LISTS = [NodeName.OrderedList, NodeName.BulletList, NodeName.Checklist];
    if (!currentNode || !ParagraphNode.is(currentNode) || !ParagraphNode.isEmpty(currentNode))
      return false;

    const { node: previousNode, offset: previousNodeOffset } =
      state.doc.childBefore(currentNodeOffset);
    if (!previousNode || !LISTS.includes(<NodeName>previousNode.type.name)) return false;

    if (dispatch) {
      const { tr } = state;
      const selectionTargetPos = previousNodeOffset + previousNode.content.size - 1;
      const resolvedPos = state.doc.resolve(selectionTargetPos);
      tr.setSelection(Selection.near(resolvedPos, -1));
      tr.delete(currentNodeOffset, currentNodeOffset + 1);
      dispatch(tr);
    }
    return true;
  }

  private getKeyBindings(schema: BasicPluginSchema) {
    const hr = schema.nodes.horizontal_rule;
    const br = schema.nodes.hard_break;
    const blockquote = schema.nodes.blockquote;
    const brCommand = chainCommands(exitCode, (state, dispatch) => {
      dispatch!(state.tr.replaceSelectionWith(br.create()).scrollIntoView());
      return true;
    });

    const keymap: Record<string, ProsemirrorCommand> = {
      ['Mod-z']: undo,
      ['Shift-Mod-z']: redo,
      ['Backspace']: undoInputRule,
      ['Alt-ArrowUp']: joinUp,
      ['Alt-ArrowDown']: joinDown,
      ['Mod-BracketLeft']: lift,
      ['Escape']: selectParentNode,
      ['Shift-Ctrl-0']: setBlockType(schema.nodes.paragraph),
      ['Mod-Enter']: brCommand,
      ['Shift-Enter']: brCommand,
      ...(this.options?.enterAsHardbreak && { ['Enter']: brCommand }),
      ...(!isMacOS && { ['Mod-y']: redo }),
      ...(isMacOS && { ['Ctrl-Enter']: brCommand }),
      ['Mod-_']: (state, dispatch) => {
        dispatch!(state.tr.replaceSelectionWith(hr.create()).scrollIntoView());
        return true;
      },
      // schema.nodes.blockquote
      ['Ctrl->']: wrapIn(blockquote),
      // schema.nodes.heading
      // ...Object.fromEntries(
      //   [...Array(5)]
      //     .map((_, i) => i + 1)
      //     .map(level => ['Shift-Ctrl-' + level, setBlockType(schema.nodes.heading, { level })]),
      // ),
    };

    return keymap;
  }
}
